import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getIntradayPnl } from '../../api/client';

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pnl = d.cumulative;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: 12,
    }}>
      <div style={{ color: 'var(--muted)', marginBottom: 4 }}>{d.time}</div>
      <div style={{ fontWeight: 700, color: pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
        {pnl >= 0 ? '+' : ''}₹{pnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </div>
      {d.symbol && d.symbol !== 'NOW' && (
        <div style={{ color: 'var(--muted)', marginTop: 3 }}>
          {d.symbol.replace(/^(NSE:|BSE:)/, '')} · {d.strategy}
        </div>
      )}
      {d.live && <div style={{ color: 'var(--accent)', marginTop: 3, fontSize: 11 }}>● Live (incl. unrealised)</div>}
    </div>
  );
}

export default function IntradayPnlChart() {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getIntradayPnl();
        setPoints(res.data.points || []);
      } catch {}
    };
    fetch();
    const t = setInterval(fetch, 10000);
    return () => clearInterval(t);
  }, []);

  const lastPnl = points.length > 0 ? points[points.length - 1].cumulative : 0;
  const color = lastPnl >= 0 ? 'var(--green)' : 'var(--red)';
  const hasData = points.length >= 2;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 600 }}>Intraday P&amp;L</div>
        <div style={{ fontWeight: 700, fontSize: 15, color }}>
          {lastPnl >= 0 ? '+' : ''}₹{lastPnl.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </div>
      </div>

      {!hasData ? (
        <div style={{
          height: 200, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'var(--muted)', fontSize: 13,
        }}>
          No closed trades today yet
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <XAxis
              dataKey="time"
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--muted)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => `₹${Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + 'k' : v}`}
              width={52}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="var(--border)" strokeDasharray="3 3" />
            <Line
              type="monotone"
              dataKey="cumulative"
              stroke={color}
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (!payload.live) return null;
                return <circle key="live" cx={cx} cy={cy} r={4} fill={color} stroke="none" />;
              }}
              activeDot={{ r: 5, fill: color }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
